import React, { useEffect, useState } from 'react';
import { fetchProducts } from './api';
import { fetchOffers } from './offersApi';
import { Box, Typography, List, ListItem, ListItemAvatar, Avatar, ListItemText, Divider, Chip, useTheme, alpha, LinearProgress } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useCurrency } from './CurrencyContext';
import { getCurrencyDecimals } from './currencyHelpers';
import { getImageUrls } from './getImageUrls';
import { useTranslation } from 'react-i18next';

const getItems = (order) => {
  if (order?.items && Array.isArray(order.items) && order.items.length > 0) return order.items;
  if (order?.OrderItemsJson) { try { const parsed = JSON.parse(order.OrderItemsJson); if (Array.isArray(parsed)) return parsed; } catch {} }
  return [];
};

const OrderPending = ({ order, stageName }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const items = getItems(order);
  const [productMap, setProductMap] = useState({});

  const { getCurrencySymbol } = useCurrency();

  // ✅ Use ONLY order currency
  const orderCurrency = order?.Currency || 'YER';
  const currencySymbol = getCurrencySymbol(orderCurrency);
  const decimals = getCurrencyDecimals(orderCurrency);
  
  const formatPrice = (amount) => {
    const num = Number(amount || 0);
    return `${num.toFixed(decimals)} ${currencySymbol}`;
  };

  useEffect(() => {
    let mounted = true;
    Promise.all([fetchProducts(), fetchOffers()]).then(([products, offers]) => {
      if (!mounted) return;
      const allOfferProducts = (offers || []).flatMap(offer => (offer.products || []).map(prod => ({ 
        ...prod, 
        Id: `${offer.offerCategoryId}_${prod.id}`, 
        ProductId: prod.id, 
        ProductName: prod.productName, 
        Price: prod.price, 
        OldPrice: prod.oldPrice, 
        ImageUrl: prod.imageJson, 
        ProductImage: prod.imageJson, 
        imageJson: prod.imageJson, 
        Description: prod.description, 
        categoryName: offer.categoryName, 
        discount: offer.discount,
        // ✅ Include ProductColors if available
        ProductColors: prod.ProductColors || prod.productColors || []
      })));
      const allProducts = [...products, ...allOfferProducts];
      const map = {};
      allProducts.forEach(p => { 
        map[String(p.Id)] = p; 
        if (p.ProductCode) map[String(p.ProductCode)] = p; 
        if (p.ProductId) map[String(p.ProductId)] = p;
      });
      setProductMap(map);
    });
    return () => { mounted = false; };
  }, []);

  return (
    <Box sx={{ bgcolor: alpha(theme.palette.background.paper, 0.95), backdropFilter: 'blur(12px)', borderRadius: '28px', boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.12)}`, p: { xs: 3, md: 4 }, m: '2rem auto', maxWidth: 650, display: 'flex', flexDirection: 'column', alignItems: 'center', fontFamily: 'Cairo, sans-serif', border: `2px solid ${alpha(theme.palette.warning.main, 0.2)}`, transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden', '&:hover': { transform: 'translateY(-8px)', boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.18)}` }, '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 6, background: `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})` } }}>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, position: 'relative', zIndex: 1 }}>
        <Box sx={{ position: 'relative', mb: 2 }}>
          <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 100, height: 100, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(theme.palette.warning.main, 0.15)} 0%, transparent 70%)`, animation: 'pulse 2s infinite' }} />
          <AccessTimeIcon sx={{ fontSize: 80, color: 'warning.main', position: 'relative', zIndex: 1, filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))', animation: 'rotate 3s linear infinite' }} />
        </Box>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 900, mb: 1.5, color: 'warning.main', textAlign: 'center', letterSpacing: '-0.5px' }}>{stageName || t('pending.pending.title')}</Typography>
        <Chip label={t('pending.pending.message')} color="warning" sx={{ fontWeight: 700, fontSize: '0.95rem', px: 3, py: 2.5, borderRadius: '16px', boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.3)}` }} />
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress sx={{ borderRadius: 2, height: 6, bgcolor: alpha(theme.palette.warning.main, 0.1), '& .MuiLinearProgress-bar': { bgcolor: 'warning.main' } }} />
        </Box>
      </Box>

      <Box sx={{ width: '100%', bgcolor: alpha(theme.palette.warning.main, 0.05), borderRadius: '20px', p: 3, mb: 3, border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}` }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="body1" fontWeight="700" color="text.secondary">{t('orderStages.common.orderNumber')}:</Typography>
          <Chip label={`#${order?.orderNumber || order?.OrderId || order?.orderId}`} size="small" sx={{ fontFamily: 'monospace', fontWeight: 800, bgcolor: 'background.paper', px: 2 }} />
        </Box>
        <Divider sx={{ my: 1.5, opacity: 0.6 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" fontWeight="700" color="text.secondary">{t('orderStages.common.orderDate')}:</Typography>
          <Typography variant="body1" fontWeight="600">{new Date(order?.orderDate || order?.CreatedDate).toLocaleDateString('ar-EG')}</Typography>
        </Box>
      </Box>

      <Box sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: '20px', p: 3, mb: 3, boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.06)}` }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 900, borderBottom: '3px solid', borderColor: 'warning.main', pb: 1.5, color: 'text.primary' }}>{t('orderStages.common.items')}</Typography>
        <List disablePadding>
          {items.length === 0 && <Typography color="text.secondary">{t('orderStages.common.noItems')}</Typography>}
          {items.map((item, idx) => {
            const code = item.ProductCode || item.ProductId || item.id || item.Id;
            let prod = productMap[String(code)] || {};
            
            // ✅ Enhanced product lookup with multiple fallbacks
            if (Object.keys(prod).length === 0) {
              // Try ProductId match
              if (item.ProductId) {
                prod = Object.values(productMap).find(p => 
                  String(p.ProductId) === String(item.ProductId) ||
                  String(p.Id) === String(item.ProductId)
                ) || {};
              }
              
              // Try name match as last resort
              if (Object.keys(prod).length === 0 && (item.name || item.ProductName)) {
                const searchName = (item.name || item.ProductName).toLowerCase();
                prod = Object.values(productMap).find(p => 
                  (p.ProductName || p.Name || '').toLowerCase() === searchName
                ) || {};
              }
            }
            
            // ✅ Merge item data with product data (item data takes priority)
            const mergedProduct = { ...prod, ...item };
            
            // Get display values with fallbacks
            const name = item.name || item.ProductName || prod.Name || prod.ProductName || `Product ${code}`;
            const description = item.Description || item.description || prod.Description || prod.description || '';
            
            // ✅ Get image with proper fallbacks
            const { thumbnail: img } = getImageUrls(mergedProduct);
            
            const qty = item.quantity || item.Quantity || 1;
            
            // ✅ Use price directly from item
            const price = Number(item.Price || item.price || 0);
            const total = Number(qty) * price;
            
            // ✅ Get selected color from item
            const selectedColor = item.SelectedColor || item.selectedColor || null;
            
            return (
              <React.Fragment key={idx}>
                <ListItem alignItems="flex-start" sx={{ px: 0, py: 2, '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.03) }, transition: 'background 0.2s', borderRadius: '12px' }}>
                  <ListItemAvatar>
                    <Avatar 
                      variant="rounded" 
                      src={img} 
                      alt={name} 
                      sx={{ 
                        width: 60, 
                        height: 60, 
                        mr: 2, 
                        border: `2px solid ${alpha(theme.palette.divider, 0.5)}`, 
                        boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}` 
                      }}
                    >
                      {name?.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                          <Typography variant="subtitle1" fontWeight="800" sx={{ mb: 0 }}>
                            {name}
                          </Typography>
                          <Chip 
                            label={`×${qty}`} 
                            size="small" 
                            sx={{ height: 20, fontSize: '0.75rem', fontWeight: 700 }} 
                          />
                        </Box>
                        
                        {/* ✅ Display Selected Color */}
                        {selectedColor && (
                          <Chip 
                            label={`🎨 ${selectedColor}`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ 
                              height: 22, 
                              fontSize: '0.7rem', 
                              fontWeight: 600,
                              alignSelf: 'flex-start'
                            }}
                          />
                        )}
                        
                        {/* ✅ Display description if available */}
                        {description && (
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ 
                              fontSize: '0.75rem',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {description}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary" fontWeight="600">
                        {formatPrice(price)} {t('orderStages.common.each', 'each')}
                      </Typography>
                    }
                  />
                  <Typography variant="subtitle1" fontWeight="900" color="warning.main" sx={{ fontSize: '1.1rem' }}>
                    {formatPrice(total)}
                  </Typography>
                </ListItem>
                
                {/* ✅ Custom Product Subproducts */}
                {item.isCustom && Array.isArray(item.SubProducts) && item.SubProducts.length > 0 && (
                  <Box sx={{ px: 1, pb: 2, bgcolor: alpha(theme.palette.info.main, 0.03), borderRadius: '12px', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'info.main', mb: 1, fontSize: '0.85rem' }}>
                      {t('customProduct.selectedSubproducts', 'Selected Options')}:
                    </Typography>
                    <List dense disablePadding>
                      {item.SubProducts.map((sp, spIdx) => {
                        if (!sp.Name) return null;
                        
                        // ✅ Use price directly from subproduct
                        const spPrice = Number(sp.Price || sp.price || 0);
                        
                        return (
                          <ListItem key={sp.Id || spIdx} sx={{ pl: 0, py: 0.5 }}>
                            <ListItemText primary={
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                <Typography variant="body2" fontWeight="700" sx={{ fontSize: '0.85rem' }}>
                                  {sp.Name}
                                </Typography>
                                {spPrice > 0 && (
                                  <Chip 
                                    label={formatPrice(spPrice)} 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined" 
                                    sx={{ height: 20, fontSize: '0.7rem' }} 
                                  />
                                )}
                                {sp.Quantity && sp.Quantity !== 0 && (
                                  <Chip 
                                    label={`Qty: ${sp.Quantity}`} 
                                    size="small" 
                                    color="secondary" 
                                    variant="outlined" 
                                    sx={{ height: 20, fontSize: '0.7rem' }} 
                                  />
                                )}
                                {sp.SizeType && (
                                  <Chip 
                                    label={sp.SizeType} 
                                    size="small" 
                                    sx={{ height: 20, fontSize: '0.7rem', bgcolor: alpha(theme.palette.info.main, 0.1) }} 
                                  />
                                )}
                                {sp.Height && sp.Height !== 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    H: {sp.Height}
                                  </Typography>
                                )}
                                {sp.Width && sp.Width !== 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    W: {sp.Width}
                                  </Typography>
                                )}
                              </Box>
                            } />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Box>
                )}
                
                {idx < items.length - 1 && <Divider sx={{ my: 1 }} />}
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      <Box sx={{ width: '100%', bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: '16px', p: 2.5, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" fontWeight="700" color="text.secondary">
            {t('orderStages.common.paymentMethod')}:
          </Typography>
          <Chip 
            label={order?.paymentMethod || t('orderStages.common.cashOnDelivery')} 
            size="small" 
            color="info" 
            variant="outlined" 
            sx={{ fontWeight: 700 }} 
          />
        </Box>
      </Box>

      <Typography variant="body2" sx={{ mt: 3, textAlign: 'center', color: 'text.secondary', fontStyle: 'italic', px: 2 }}>
        {t('pending.pending.footer', 'سيتم تحديث الحالة قريباً')}
      </Typography>

      <style>{`
        @keyframes pulse { 
          0%, 100% { opacity: 1; transform: scale(1); } 
          50% { opacity: 0.8; transform: scale(1.05); } 
        } 
        @keyframes rotate { 
          0% { transform: rotate(0deg); } 
          100% { transform: rotate(360deg); } 
        }
      `}</style>
    </Box>
  );
};

export default OrderPending;